/**
 * Calendar Navigation Hook
 *
 * Provides state and navigation for calendar views.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  isSameDay,
  isSameMonth,
  isToday,
  format,
  getDay,
} from 'date-fns';
import { it } from 'date-fns/locale';
import type { Task } from '@/types';

export type CalendarViewMode = 'month' | 'week' | 'day';

export interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  tasks: Task[];
}

export interface CalendarWeek {
  days: CalendarDay[];
  weekNumber: number;
}

interface UseCalendarOptions {
  tasks: Task[];
  initialDate?: Date;
  initialViewMode?: CalendarViewMode;
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0 = Sunday, 1 = Monday
}

interface UseCalendarReturn {
  // Navigation state
  currentDate: Date;
  viewMode: CalendarViewMode;

  // Derived data
  calendarDays: CalendarDay[];
  calendarWeeks: CalendarWeek[];
  monthLabel: string;
  weekLabel: string;
  dayLabel: string;

  // Navigation
  goToNext: () => void;
  goToPrev: () => void;
  goToToday: () => void;
  goToDate: (date: Date) => void;
  setViewMode: (mode: CalendarViewMode) => void;

  // Task helpers
  getTasksForDate: (date: Date) => Task[];
  getDayWithMostTasks: () => CalendarDay | null;
  getTotalTasksInView: () => number;
}

export function useCalendar({
  tasks,
  initialDate = new Date(),
  initialViewMode = 'month',
  weekStartsOn = 1, // Monday
}: UseCalendarOptions): UseCalendarReturn {
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [viewMode, setViewMode] = useState<CalendarViewMode>(initialViewMode);

  // Get tasks for a specific date (by dueDate or scheduledStart)
  const getTasksForDate = useCallback(
    (date: Date): Task[] => {
      return tasks.filter((task) => {
        // Match by scheduledStart
        if (task.scheduledStart && isSameDay(new Date(task.scheduledStart), date)) {
          return true;
        }
        // Match by dueDate
        if (task.dueDate && isSameDay(new Date(task.dueDate), date)) {
          return true;
        }
        return false;
      });
    },
    [tasks]
  );

  // Calculate calendar days based on view mode
  const calendarDays = useMemo((): CalendarDay[] => {
    let start: Date;
    let end: Date;

    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate);
      const monthEnd = endOfMonth(currentDate);
      start = startOfWeek(monthStart, { weekStartsOn });
      end = endOfWeek(monthEnd, { weekStartsOn });
    } else if (viewMode === 'week') {
      start = startOfWeek(currentDate, { weekStartsOn });
      end = endOfWeek(currentDate, { weekStartsOn });
    } else {
      // Day view
      start = currentDate;
      end = currentDate;
    }

    const days = eachDayOfInterval({ start, end });

    return days.map((date) => ({
      date,
      isCurrentMonth: isSameMonth(date, currentDate),
      isToday: isToday(date),
      isWeekend: getDay(date) === 0 || getDay(date) === 6,
      tasks: getTasksForDate(date),
    }));
  }, [currentDate, viewMode, weekStartsOn, getTasksForDate]);

  // Group days into weeks
  const calendarWeeks = useMemo((): CalendarWeek[] => {
    const weeks: CalendarWeek[] = [];
    let currentWeek: CalendarDay[] = [];

    calendarDays.forEach((day, index) => {
      currentWeek.push(day);
      if ((index + 1) % 7 === 0) {
        weeks.push({
          days: currentWeek,
          weekNumber: Math.ceil((index + 1) / 7),
        });
        currentWeek = [];
      }
    });

    // Handle remaining days
    if (currentWeek.length > 0) {
      weeks.push({
        days: currentWeek,
        weekNumber: weeks.length + 1,
      });
    }

    return weeks;
  }, [calendarDays]);

  // Navigation labels
  const monthLabel = useMemo(
    () => format(currentDate, 'MMMM yyyy', { locale: it }),
    [currentDate]
  );

  const weekLabel = useMemo(() => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn });
    return `${format(weekStart, 'd MMM', { locale: it })} - ${format(weekEnd, 'd MMM yyyy', { locale: it })}`;
  }, [currentDate, weekStartsOn]);

  const dayLabel = useMemo(
    () => format(currentDate, 'EEEE d MMMM yyyy', { locale: it }),
    [currentDate]
  );

  // Navigation functions
  const goToNext = useCallback(() => {
    if (viewMode === 'month') {
      setCurrentDate((d) => addMonths(d, 1));
    } else if (viewMode === 'week') {
      setCurrentDate((d) => addWeeks(d, 1));
    } else {
      setCurrentDate((d) => addDays(d, 1));
    }
  }, [viewMode]);

  const goToPrev = useCallback(() => {
    if (viewMode === 'month') {
      setCurrentDate((d) => subMonths(d, 1));
    } else if (viewMode === 'week') {
      setCurrentDate((d) => subWeeks(d, 1));
    } else {
      setCurrentDate((d) => subDays(d, 1));
    }
  }, [viewMode]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const goToDate = useCallback((date: Date) => {
    setCurrentDate(date);
  }, []);

  // Helper functions
  const getDayWithMostTasks = useCallback((): CalendarDay | null => {
    if (calendarDays.length === 0) return null;
    return calendarDays.reduce((max, day) =>
      day.tasks.length > max.tasks.length ? day : max
    );
  }, [calendarDays]);

  const getTotalTasksInView = useCallback((): number => {
    return calendarDays.reduce((sum, day) => sum + day.tasks.length, 0);
  }, [calendarDays]);

  return {
    // Navigation state
    currentDate,
    viewMode,

    // Derived data
    calendarDays,
    calendarWeeks,
    monthLabel,
    weekLabel,
    dayLabel,

    // Navigation
    goToNext,
    goToPrev,
    goToToday,
    goToDate,
    setViewMode,

    // Task helpers
    getTasksForDate,
    getDayWithMostTasks,
    getTotalTasksInView,
  };
}
